import React, { useMemo } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { InkSkeleton } from '../cms/InkSkeleton';
import { RollNumber } from '../common/RollNumber';
import { neverApproved, phaseOf } from './campaignPhase';
import { calendarDate, campaignRange } from './reach';
import { CampaignSummary, campaignTone, compactCount, firstName } from './campaignCards';

/**
 * Campaigns → Analytics (TinyCMS UX optimization review, 2026-10-23): every
 * campaign's work and results added up — the totals on top, then one row per
 * campaign with its status, its work ready, posts out, impressions, clicks to
 * the site, landing page views, sign-ups and conversion (sign-ups per click).
 * The apiserver counts them (reach/services/campaignAnalytics.js).
 */

export const CAMPAIGN_ANALYTICS = gql`
  query CampaignAnalytics($accountId: ID!) {
    campaignAnalytics(accountId: $accountId)
  }
`;

interface CampaignStats extends Omit<CampaignSummary, 'id'> {
  campaignId: string;
  waiting: boolean;
  work: { ready: number; total: number };
  results: {
    posts: number;
    impressions: number | null;
    clicks: number;
    signups: number;
    conversion: number | null;
  } | null;
  /** The landing page's views over the last 90 days: the page's, not the campaign's. */
  pageViews: number | null;
  /** The landing page's path on the site; campaigns can share one. */
  landingPath?: string | null;
}

const pctText = (n: number | null | undefined) => (n == null ? '—' : `${(n * 100).toFixed(1)}%`);

export function CampaignAnalytics() {
  const navigate = useNavigate();
  const { accountId, selectedAccount } = useAccount();
  const campaignRole = Boolean(selectedAccount?.campaignRole);
  const approver = selectedAccount?.approverName ? firstName(selectedAccount.approverName) : null;
  const { data, loading, error, refetch } = useQuery<{ campaignAnalytics: CampaignStats[] }>(
    CAMPAIGN_ANALYTICS,
    { variables: { accountId }, skip: !accountId, fetchPolicy: 'cache-and-network' }
  );

  const rows = useMemo(() => {
    const now = new Date();
    return (data?.campaignAnalytics || [])
      .map((c) => {
        const summary = { ...c, id: c.campaignId };
        const phase = phaseOf(summary, now);
        const tone = campaignTone(
          phase,
          summary,
          { waiting: phase !== 'ended' && c.waiting, canApprove: !campaignRole, approver },
          neverApproved(summary)
        );
        const start = calendarDate(c.startDate) || (c.createdAt ? new Date(c.createdAt) : null);
        const started = !start || start.getTime() <= now.getTime();
        // Results are measured once a post is out; before that they're "—", not 0.
        const measured = Boolean(c.results && c.results.posts);
        // A campaign that hasn't begun has no page views of its own: its landing
        // page's traffic so far is ordinary traffic (backlog f688a851).
        const views = started ? c.pageViews : null;
        return { c, tone, phase, started, measured, views };
      })
      .sort(
        (a, b) =>
          (a.phase === 'ended' ? 1 : 0) - (b.phase === 'ended' ? 1 : 0) ||
          (calendarDate(b.c.startDate)?.getTime() || 0) -
            (calendarDate(a.c.startDate)?.getTime() || 0)
      );
  }, [data, campaignRole, approver]);

  // How many campaigns use each landing page, so a shared page says so.
  const sharing = useMemo(() => {
    const n = new Map<string, number>();
    rows.forEach(({ c }) => {
      if (c.landingPath) n.set(c.landingPath, (n.get(c.landingPath) || 0) + 1);
    });
    return n;
  }, [rows]);

  // One rule in the cards, the rows and the total: "—" when there's nothing to
  // measure yet (no posts out), a number (0 included) once there is. A landing
  // page is added once however many campaigns share it.
  const viewedPaths = new Set<string>();
  const tot = rows.reduce(
    (a, { c, started, measured, views }) => {
      a.ready += c.work.ready;
      a.total += c.work.total;
      if (started) a.started = true;
      if (c.results && measured) {
        a.measured = true;
        a.posts += c.results.posts;
        a.clicks += c.results.clicks;
        a.signups += c.results.signups;
        if (c.results.impressions != null) {
          a.imps += c.results.impressions;
          a.hasImps = true;
        }
      }
      if (views != null) {
        const key = c.landingPath || c.campaignId;
        if (!viewedPaths.has(key)) a.views += views;
        viewedPaths.add(key);
        a.hasViews = true;
      }
      return a;
    },
    {
      ready: 0,
      total: 0,
      posts: 0,
      imps: 0,
      clicks: 0,
      signups: 0,
      views: 0,
      hasImps: false,
      hasViews: false,
      started: false,
      measured: false,
    }
  );
  const conv = tot.clicks ? tot.signups / tot.clicks : null;
  const measuredCount = (n: number) => (tot.measured ? compactCount(n) : '—');

  if (loading && !data)
    return (
      <div className="can">
        <InkSkeleton rows={5} label="Loading campaign results" style={{ marginTop: 28 }} />
      </div>
    );
  if (error && !data) {
    return (
      <p className="ink-muted" role="alert" style={{ marginTop: 28 }}>
        Campaign results couldn’t load just now.{' '}
        <button type="button" className="ink-quiet-link" onClick={() => void refetch()}>
          Try again
        </button>
      </p>
    );
  }

  const kpis: Array<[string, string, string]> = [
    ['Impressions', tot.hasImps ? compactCount(tot.imps) : '—', 'Across every campaign post'],
    ['Clicks to your site', measuredCount(tot.clicks), 'From tracked campaign links'],
    [
      'Landing page views',
      tot.hasViews ? compactCount(tot.views) : '—',
      [...sharing.values()].some((n) => n > 1)
        ? 'Last 90 days, each page counted once'
        : 'Last 90 days, including direct and search',
    ],
    [
      'Sign-ups',
      measuredCount(tot.signups),
      conv == null ? 'No clicks yet' : `${pctText(conv)} of clicks`,
    ],
    ['Work ready', `${tot.ready} / ${tot.total}`, 'Posts, pages, articles and tasks'],
  ];

  return (
    <div className="can">
      <div className="can-head">
        <span className="can-title">All campaigns</span>
        <span className="can-sub">Every post, page and form in each campaign, added up</span>
      </div>
      <div className="can-kpis">
        {kpis.map(([label, n, sub]) => (
          <div className="can-kpi" key={label}>
            <div className="can-kpi-label">{label}</div>
            <div className="can-kpi-n">
              <RollNumber value={n} />
            </div>
            <div className="can-kpi-sub">{sub}</div>
          </div>
        ))}
      </div>
      {rows.length ? (
        <div className="can-scroll">
          <div className="can-table" role="table" aria-label="Campaign results">
            <div className="can-row can-row-head" role="row">
              <span>Campaign</span>
              <span>Status</span>
              <span>Work</span>
              <span className="can-num">Posts out</span>
              <span className="can-num">Impressions</span>
              <span className="can-num">Clicks</span>
              <span className="can-num">Page views</span>
              <span className="can-num">Sign-ups</span>
              <span className="can-num">Conversion</span>
            </div>
            {rows.map(({ c, tone, started, measured: has, views }) => {
              const r = c.results;
              const full = c.work.total > 0 && c.work.ready === c.work.total;
              const others = c.landingPath ? (sharing.get(c.landingPath) || 1) - 1 : 0;
              return (
                <button
                  type="button"
                  role="row"
                  className="can-row can-row-body"
                  key={c.campaignId}
                  onClick={() =>
                    navigate(`/campaigns?campaign=${encodeURIComponent(c.campaignId)}`)
                  }
                >
                  <span className="can-name">
                    <span className="can-name-t">{c.name}</span>
                    <span className="can-dates">{campaignRange(c)}</span>
                    {others ? (
                      <span
                        className="can-dates"
                        title="These campaigns share one landing page, so they show the same page views."
                      >
                        Landing page shared with {others} other{' '}
                        {others === 1 ? 'campaign' : 'campaigns'}
                      </span>
                    ) : null}
                  </span>
                  <span className="can-status" style={{ color: tone.ink }}>
                    <span className="cb-dot" style={{ background: tone.dot }} />
                    {tone.label}
                  </span>
                  <span className="can-work">
                    <span>
                      {c.work.total
                        ? `${c.work.ready} of ${c.work.total} ready`
                        : 'Nothing planned'}
                    </span>
                    <span className="can-meter">
                      <span
                        style={{
                          width: `${c.work.total ? Math.round((c.work.ready / c.work.total) * 100) : 0}%`,
                          background: full ? 'var(--ink-green)' : 'var(--ink-accent)',
                        }}
                      />
                    </span>
                  </span>
                  <span className="can-num">{has ? r!.posts : started ? '0' : '—'}</span>
                  <span className="can-num">{has ? compactCount(r!.impressions) : '—'}</span>
                  <span className="can-num">{has ? compactCount(r!.clicks) : '—'}</span>
                  <span
                    className="can-num"
                    title={
                      started ? undefined : 'Not started yet. Page views count once it starts.'
                    }
                  >
                    {started ? compactCount(views) : '—'}
                  </span>
                  <span className="can-num can-strong">{has ? compactCount(r!.signups) : '—'}</span>
                  <span className="can-num">{has ? pctText(r!.conversion) : '—'}</span>
                </button>
              );
            })}
            <div className="can-row can-row-total" role="row">
              <span>Total</span>
              <span />
              <span className="can-work-total">
                {tot.ready} of {tot.total} ready
              </span>
              <span className="can-num">{tot.started ? tot.posts : '—'}</span>
              <span className="can-num">{tot.hasImps ? compactCount(tot.imps) : '—'}</span>
              <span className="can-num">{measuredCount(tot.clicks)}</span>
              <span className="can-num">{tot.hasViews ? compactCount(tot.views) : '—'}</span>
              <span className="can-num">{measuredCount(tot.signups)}</span>
              <span className="can-num">{pctText(conv)}</span>
            </div>
          </div>
        </div>
      ) : (
        <p className="ink-muted" style={{ marginTop: 20 }}>
          No campaigns yet. Start one under Campaigns.
        </p>
      )}
      <p className="ccal-foot">
        Impressions come from each network. Clicks and sign-ups are counted by your site from each
        campaign’s tracked links. Landing page views come from Google Analytics: all of a page’s
        views over the last 90 days, not only the campaign’s, and a page several campaigns share is
        counted once in the total. Campaigns that haven’t started show their work so far.
      </p>
    </div>
  );
}
